import React, { useState, useEffect, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";
import {
  CircularProgress,
  OutlinedInput,
  IconButton,
  InputAdornment,
  Button,
  Stack,
  Box,
  Tooltip,
  useTheme,
  Tabs,
  Tab,
  Paper,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from "@mui/material";
import {
  Search,
  Search as SearchIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  ZoomIn as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";

import { getAllUsers } from "features/appUsers/appUsers.slice";
import { getRegistrationRequests, removeRegistrationRequest } from "features/auth/auth.slice";
import appUsersService from "features/appUsers/service";
import { readAsyncStorageValues } from "features/service/service.slice";
import "../../App.css";
import "../../stylesheet/charts.css";
import { useNavigate } from "react-router-dom";
import { Links } from "utils/pageLinkNames";
import Header from "component/Header";
import DataGridTable from "component/DataGridTable";
import TableSkeleton from "component/Loaders/TableSkeleton";
import { exportToCsv } from "utils/utilFunctions";
import { buildRegistrationRejectedMessage } from "utils/registrationRequestMessaging";
import RegistrationNotifyDialog from "./components/RegistrationNotifyDialog";
import DepartmentFormDialog from "./components/DepartmentFormDialog";
import DeleteDepartmentDialog from "./components/DeleteDepartmentDialog";
import RejectRegistrationDialog from "./components/RejectRegistrationDialog";
import FilterHeaderDropdown from "component/ui/FilterHeaderDropdown";

const Users = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();

  const { allUsers, isLoading } = useSelector((state) => state.appUsers);
  const { mode } = useSelector((state) => state.globalState);
  const {
    registrationRequests,
    registrationRequestsLoading: requestsLoading,
    registrationRequestsError: requestsError,
  } = useSelector((state) => state.auth);

  useEffect(() => {
    dispatch(getAllUsers());
    dispatch(readAsyncStorageValues());
  }, [dispatch]);

  const [searchValue, setSearchValue] = useState("");
  const [usersGridFilters, setUsersGridFilters] = useState({
    userRole: "",
    department: "",
  });
  const [activeTab, setActiveTab] = useState("users");
  const [userStatusTab, setUserStatusTab] = useState("active");
  const [departmentSearchValue, setDepartmentSearchValue] = useState("");
  const [requestsSearchValue, setRequestsSearchValue] = useState("");
  const [requestActionLoading, setRequestActionLoading] = useState({});
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [departmentsError, setDepartmentsError] = useState("");
  const [createDeptDialogOpen, setCreateDeptDialogOpen] = useState(false);
  const [newDepartmentName, setNewDepartmentName] = useState("");
  const [createDeptSubmitting, setCreateDeptSubmitting] = useState(false);
  const [editDeptDialogOpen, setEditDeptDialogOpen] = useState(false);
  const [editDeptRow, setEditDeptRow] = useState(null);
  const [editDeptNameInput, setEditDeptNameInput] = useState("");
  const [editDeptSubmitting, setEditDeptSubmitting] = useState(false);
  const [deleteDeptDialogOpen, setDeleteDeptDialogOpen] = useState(false);
  const [deleteDeptRow, setDeleteDeptRow] = useState(null);
  const [deleteDeptUsers, setDeleteDeptUsers] = useState([]);
  const [deleteDeptUsersLoading, setDeleteDeptUsersLoading] = useState(false);
  const [deleteDeptUsersError, setDeleteDeptUsersError] = useState("");
  const [deleteDeptSubmitting, setDeleteDeptSubmitting] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectDialogRequest, setRejectDialogRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectReasonError, setRejectReasonError] = useState("");
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [rejectNotifyDialog, setRejectNotifyDialog] = useState({
    open: false,
    message: "",
    mobileNumber: "",
    recipientName: "",
    recipientMeta: "",
  });

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const handleClearUsersSearch = () => setSearchValue("");
  const handleDepartmentSearchChange = (event) => {
    setDepartmentSearchValue(event.target.value);
  };
  const handleClearDepartmentSearch = () => setDepartmentSearchValue("");

  const closeCreateDeptDialog = () => {
    if (createDeptSubmitting) return;
    setCreateDeptDialogOpen(false);
    setNewDepartmentName("");
  };

  const submitCreateDepartment = async () => {
    const name = newDepartmentName.trim();
    if (!name) {
      toast.error("Department name is required");
      return;
    }
    setCreateDeptSubmitting(true);
    try {
      const data = await appUsersService.createDepartment({ name });
      toast.success(data?.message || "Department created successfully");
      const d = data?.department;
      if (d && d.id != null) {
        setDepartments((prev) => {
          const next = [...(prev || []), { id: d.id, name: d.name, userCount: 0 }];
          next.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
          return next;
        });
      }
      setCreateDeptDialogOpen(false);
      setNewDepartmentName("");
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed to create department";
      toast.error(String(msg));
    } finally {
      setCreateDeptSubmitting(false);
    }
  };

  const resetEditDeptForm = () => {
    setEditDeptDialogOpen(false);
    setEditDeptRow(null);
    setEditDeptNameInput("");
  };

  const closeEditDeptDialog = () => {
    if (editDeptSubmitting) return;
    resetEditDeptForm();
  };

  const openEditDepartment = (row) => {
    if (!row || row.id == null) return;
    setEditDeptRow({ id: row.id, name: row.name, userCount: row.userCount ?? 0 });
    setEditDeptNameInput(row.name ?? "");
    setEditDeptDialogOpen(true);
  };

  const submitEditDepartment = async () => {
    if (!editDeptRow) return;
    const name = editDeptNameInput.trim();
    if (!name) {
      toast.error("Department name is required");
      return;
    }
    setEditDeptSubmitting(true);
    try {
      const data = await appUsersService.updateDepartment({ id: editDeptRow.id, name });
      toast.success(data?.message || "Department updated successfully");
      const d = data?.department;
      if (d && d.id != null) {
        setDepartments((prev) => {
          const next = (prev || []).map((dept) => (String(dept.id) === String(d.id) ? { ...dept, id: d.id, name: d.name } : dept));
          next.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
          return next;
        });
      }
      try {
        await dispatch(getAllUsers()).unwrap();
      } catch {
        /* list refresh failed */
      }
      resetEditDeptForm();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed to update department";
      toast.error(String(msg));
    } finally {
      setEditDeptSubmitting(false);
    }
  };

  const resetDeleteDeptDialog = () => {
    setDeleteDeptDialogOpen(false);
    setDeleteDeptRow(null);
    setDeleteDeptUsers([]);
    setDeleteDeptUsersError("");
    setDeleteDeptUsersLoading(false);
  };

  const closeDeleteDeptDialog = () => {
    if (deleteDeptSubmitting) return;
    resetDeleteDeptDialog();
  };

  const openDeleteDepartment = async (row) => {
    if (!row || row.id == null) return;
    setDeleteDeptRow({ id: row.id, name: row.name ?? "", userCount: row.userCount ?? 0 });
    setDeleteDeptUsers([]);
    setDeleteDeptUsersError("");
    setDeleteDeptDialogOpen(true);
    setDeleteDeptUsersLoading(true);
    try {
      const data = await appUsersService.getDepartmentUsers(row.id);
      setDeleteDeptUsers(Array.isArray(data?.users) ? data.users : []);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Could not load department users";
      setDeleteDeptUsersError(String(msg));
      setDeleteDeptUsers([]);
    } finally {
      setDeleteDeptUsersLoading(false);
    }
  };

  const submitDeleteDepartment = async () => {
    if (!deleteDeptRow) return;
    if (deleteDeptUsersLoading) return;
    if (deleteDeptUsers.length > 0) return;

    setDeleteDeptSubmitting(true);
    try {
      const data = await appUsersService.deleteDepartment(deleteDeptRow.id);
      toast.success(data?.message || "Department deleted successfully");
      setDepartments((prev) => (prev || []).filter((d) => String(d.id) !== String(deleteDeptRow.id)));
      try {
        await dispatch(getAllUsers()).unwrap();
      } catch {
        /* list refresh failed */
      }
      resetDeleteDeptDialog();
    } catch (e) {
      const payload = e?.response?.data ?? e;
      let msg = payload?.message || e?.message || "Failed to delete department";
      if (payload?.userCount != null) {
        msg = `${msg} (${payload.userCount} user${payload.userCount === 1 ? "" : "s"})`;
      }
      toast.error(String(msg));
    } finally {
      setDeleteDeptSubmitting(false);
    }
  };

  const handleRequestsSearchChange = (event) => {
    setRequestsSearchValue(event.target.value);
  };
  const handleClearRequestsSearch = () => setRequestsSearchValue("");

  const resetRejectDialog = () => {
    setRejectDialogOpen(false);
    setRejectDialogRequest(null);
    setRejectReason("");
    setRejectReasonError("");
    setRejectSubmitting(false);
  };

  const closeRejectDialog = () => {
    if (rejectSubmitting) return;
    resetRejectDialog();
  };

  const openRejectDialog = (row) => {
    if (!row?.uuid) return;
    setRejectDialogRequest(row);
    setRejectReason("");
    setRejectReasonError("");
    setRejectDialogOpen(true);
  };

  const submitRejectRegistrationRequest = async () => {
    const uuid = rejectDialogRequest?.uuid;
    if (!uuid) return;

    const inactiveReason = (rejectReason ?? "").trim();
    if (!inactiveReason) {
      setRejectReasonError("Reason is required");
      return;
    }

    setRejectReasonError("");
    setRejectSubmitting(true);
    setRequestActionLoading((prev) => ({
      ...(prev || {}),
      [uuid]: { ...(prev?.[uuid] || {}), reject: true },
    }));

    try {
      const resp = await dispatch(removeRegistrationRequest({ uuid, inactiveReason })).unwrap();
      toast.success(resp?.message || "Registration request rejected");

      const whatsappMessage = buildRegistrationRejectedMessage({
        name: rejectDialogRequest?.name,
        employeeId: rejectDialogRequest?.employeeId,
        inactiveReason,
      });

      const rejectedRow = rejectDialogRequest;
      resetRejectDialog();
      setRejectNotifyDialog({
        open: true,
        message: whatsappMessage,
        mobileNumber: rejectedRow?.mobileNumber || "",
        recipientName: rejectedRow?.name || "",
        recipientMeta: rejectedRow?.employeeId ? `Employee ID: ${rejectedRow.employeeId}` : "",
      });

      try {
        await dispatch(getAllUsers()).unwrap();
      } catch {
        /* list refresh failed */
      }
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.message || e?.message || "Failed to reject request";
      toast.error(String(msg));
    } finally {
      setRejectSubmitting(false);
      setRequestActionLoading((prev) => {
        const next = { ...(prev || {}) };
        if (next[uuid]) {
          next[uuid] = { ...next[uuid], reject: false };
        }
        return next;
      });
    }
  };

  const activeUsersCount = (allUsers || []).filter((u) => !!u.enabled).length;
  const inactiveUsersCount = (allUsers || []).filter((u) => !u.enabled).length;
  const newRequestsCount = (registrationRequests || []).length;

  const filteredRegistrationRequestsForGrid = (registrationRequests || []).filter((item) =>
    Object.values(item || {})
      .join(" ")
      .toLowerCase()
      .includes(requestsSearchValue.toLowerCase()),
  );

  useEffect(() => {
    // Fetch as soon as the Users page/tab is opened so the count is correct.
    if (activeTab === "users") {
      dispatch(getRegistrationRequests());
    }
  }, [activeTab, dispatch]);

  useEffect(() => {
    let isMounted = true;

    async function loadDepartments() {
      setDepartmentsLoading(true);
      setDepartmentsError("");
      try {
        const list = await appUsersService.getAllDepartments();
        if (!isMounted) return;
        setDepartments(list);
      } catch (e) {
        if (!isMounted) return;
        const msg = e?.response?.data?.message || e?.message || e?.error || "Failed to load departments";
        setDepartmentsError(String(msg));
        setDepartments([]);
      } finally {
        if (!isMounted) return;
        setDepartmentsLoading(false);
      }
    }

    if (activeTab === "departments") {
      loadDepartments();
    }

    return () => {
      isMounted = false;
    };
  }, [activeTab]);

  useEffect(() => {
    setUsersGridFilters({ userRole: "", department: "" });
    setSearchValue("");
  }, [userStatusTab]);

  const usersForCurrentStatusTab = (allUsers || []).filter((u) => {
    if (userStatusTab === "active") return !!u.enabled;
    if (userStatusTab === "inactive") return !u.enabled;
    return false;
  });

  const getUserRoleValue = (user) =>
    user?.userRole != null && user.userRole !== "" ? String(user.userRole).trim() : "";
  const getUserDepartmentValue = (user) =>
    user?.department != null && user.department !== "" ? String(user.department).trim() : "";

  const getUsersMatchingGridFilters = ({ userRole = "", department = "" } = {}) =>
    usersForCurrentStatusTab.filter((user) => {
      if (userRole && getUserRoleValue(user) !== userRole) return false;
      if (department && getUserDepartmentValue(user) !== department) return false;
      return true;
    });

  // Role options respect the selected department; department options respect the selected role.
  const { userGridRoleOptions, userGridDepartmentOptions } = useMemo(() => {
    const roles = new Set();
    for (const u of getUsersMatchingGridFilters({ department: usersGridFilters.department })) {
      const r = getUserRoleValue(u);
      if (r) roles.add(r);
    }
    const depts = new Set();
    for (const u of getUsersMatchingGridFilters({ userRole: usersGridFilters.userRole })) {
      const d = getUserDepartmentValue(u);
      if (d) depts.add(d);
    }
    return {
      userGridRoleOptions: [...roles],
      userGridDepartmentOptions: [...depts],
    };
  }, [allUsers, userStatusTab, usersGridFilters.userRole, usersGridFilters.department]);

  const filteredUsersForGrid = usersForCurrentStatusTab
    .filter((item) => Object.values(item).join(" ").toLowerCase().includes(searchValue.toLowerCase()))
    .filter((item) => {
      if (!usersGridFilters.userRole) return true;
      return getUserRoleValue(item) === usersGridFilters.userRole;
    })
    .filter((item) => {
      if (!usersGridFilters.department) return true;
      return getUserDepartmentValue(item) === usersGridFilters.department;
    });

  const handleUserRoleFilterChange = (value) => {
    setUsersGridFilters((prev) => {
      let nextDepartment = prev.department;
      if (prev.department && value) {
        const deptsForRole = new Set(
          getUsersMatchingGridFilters({ userRole: value }).map(getUserDepartmentValue).filter(Boolean)
        );
        if (!deptsForRole.has(prev.department)) {
          nextDepartment = "";
        }
      }
      return { ...prev, userRole: value, department: nextDepartment };
    });
  };

  const handleUserDepartmentFilterChange = (value) => {
    setUsersGridFilters((prev) => {
      let nextRole = prev.userRole;
      if (prev.userRole && value) {
        const rolesForDept = new Set(
          getUsersMatchingGridFilters({ department: value }).map(getUserRoleValue).filter(Boolean)
        );
        if (!rolesForDept.has(prev.userRole)) {
          nextRole = "";
        }
      }
      return { ...prev, department: value, userRole: nextRole };
    });
  };

  const clearAllUsersGridFilters = () => {
    setUsersGridFilters({
      userRole: "",
      department: "",
    });
    setSearchValue("");
  };

  const USERS_GRID_FILTER_LABELS = {
    userRole: "Role",
    department: "Department",
  };

  const parseInactiveReasonFromInfo = (info) => {
    if (!info) return "";
    try {
      const infoMap = typeof info === "string" ? JSON.parse(info) : info;
      return infoMap?.inactiveReason ? String(infoMap.inactiveReason).trim() : "";
    } catch {
      return "";
    }
  };

  const parseResponsibleForFacilitiesTicketsFromInfo = (info) => {
    if (!info) return false;
    try {
      const infoMap = typeof info === "string" ? JSON.parse(info) : info;
      return !!infoMap?.responsible_for_facilities_tickets;
    } catch {
      return false;
    }
  };

  const renderUserRoleCell = (params) => {
    const role = params.row?.userRole || "";
    const showFacilityChip =
      role === "ROLE_MAINTENANCE_SUPERVISOR" && parseResponsibleForFacilitiesTicketsFromInfo(params.row?.info);

    return (
      <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
        <span>{role}</span>
        {showFacilityChip ? (
          <Chip label="Facility" size="small" sx={{ color: "#ffffff", backgroundColor: "#00b609" }} />
        ) : null}
      </Stack>
    );
  };

  const downloadCsv = (e) => {
    e.preventDefault();
    if (userStatusTab === "requests") return;
    const isInactiveTab = userStatusTab === "inactive";
    const headers = isInactiveTab
      ? ["User Name,Inactive reason,Name,Email,Mobile Number,Role,Department,Active"]
      : ["User Name,Name,Email,Mobile Number,Role,Department,Active"];
    const keys = isInactiveTab
      ? ["userName", "inactiveReason", "name", "email", "userMobileNumber", "userRole", "department", "enabled"]
      : ["userName", "name", "email", "userMobileNumber", "userRole", "department", "enabled"];
    const rows = isInactiveTab
      ? filteredUsersForGrid.map((user) => ({
          ...user,
          inactiveReason: parseInactiveReasonFromInfo(user?.info),
        }))
      : filteredUsersForGrid;
    exportToCsv(headers, keys, rows, `Users_${userStatusTab}`);
  };

  const canExportUsersCsv = userStatusTab !== "requests" && (filteredUsersForGrid || []).length > 0;

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return "";
    const digitsOnly = phoneNumber.replace(/\D/g, "");
    const countryCode = digitsOnly.slice(0, 2);
    const localNumber = digitsOnly.slice(2);

    if (localNumber.length === 10) {
      return `+${countryCode} ${localNumber.slice(0, 3)} ${localNumber.slice(3, 6)} ${localNumber.slice(6, 10)}`;
    } else if (localNumber.length === 8) {
      return `+${countryCode} ${localNumber.slice(0, 4)} ${localNumber.slice(4, 8)}`;
    } else {
      return phoneNumber;
    }
  };

  const gridUserAction = (args) => {
    return (
      <div>
        <IconButton sx={{ mr: 1 }} onClick={() => navigate(`/${Links.EDIT_USER}/` + args.row.userId)}>
          <Tooltip title="View">
            <ViewIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
          </Tooltip>
        </IconButton>
        <IconButton sx={{ mr: 1 }} onClick={() => navigate(`/${Links.EDIT_USER}/` + args.row.userId, { state: { enableEditing: true } })}>
          <Tooltip title="Edit">
            <EditIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
          </Tooltip>
        </IconButton>
      </div>
    );
  };

  const gridDepartmentAction = (args) => {
    return (
      <div>
        <IconButton sx={{ mr: 1 }} onClick={() => openEditDepartment(args.row)}>
          <Tooltip title="Edit">
            <EditIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
          </Tooltip>
        </IconButton>
        <IconButton sx={{ mr: 1 }} onClick={() => openDeleteDepartment(args.row)}>
          <Tooltip title="Delete">
            <DeleteIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
          </Tooltip>
        </IconButton>
      </div>
    );
  };
  const userGrid = useMemo(() => {
    const columns = [
      {
        field: "name",
        headerName: "Name",
        minWidth: 50,
        flex: 0.3,
      },
      {
        field: "email",
        headerName: "Email",
        minWidth: 50,
        flex: 0.3,
      },
      {
        field: "userMobileNumber",
        headerName: "Mobile Number",
        minWidth: 60,
        flex: 0.22,
        valueGetter: (params) => formatPhoneNumber(params.row.userMobileNumber),
      },
      {
        field: "userRole",
        headerName: "Role",
        minWidth: 260,
        flex: 0.45,
        renderCell: renderUserRoleCell,
        renderHeader: () => (
          <FilterHeaderDropdown
            label="Role"
            value={usersGridFilters.userRole}
            options={userGridRoleOptions}
            onChange={handleUserRoleFilterChange}
          />
        ),
      },
      {
        field: "department",
        headerName: "Department",
        minWidth: 100,
        flex: 0.3,
        renderHeader: () => (
          <FilterHeaderDropdown
            label="Department"
            value={usersGridFilters.department}
            options={userGridDepartmentOptions}
            onChange={handleUserDepartmentFilterChange}
          />
        ),
      },
      {
        field: "userName",
        headerName: "User Name",
        minWidth: 30,
        flex: 0.2,
      },
    ];

    if (userStatusTab === "inactive") {
      columns.push({
        field: "inactiveReason",
        headerName: "Inactive reason",
        minWidth: 160,
        flex: 0.5,
        valueGetter: (params) => parseInactiveReasonFromInfo(params.row?.info),
      });
    }

    columns.push({
      headerName: "",
      minWidth: 40,
      flex: 0.2,
      renderCell: gridUserAction,
      sortable: false,
      filterable: false,
    });

    return columns;
  }, [userStatusTab, usersGridFilters, userGridRoleOptions, userGridDepartmentOptions, mode, theme, navigate]);

  const filteredDepartmentsForGrid = departments
    .slice()
    .sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
    .filter((dept) =>
      Object.values(dept || {})
        .join(" ")
        .toLowerCase()
        .includes(departmentSearchValue.toLowerCase()),
    );

  const departmentGrid = [
    {
      field: "name",
      headerName: "Department Name",
      minWidth: 120,
      flex: 0.55,
      valueGetter: (params) => params?.row?.name ?? "",
    },
    {
      field: "userCount",
      headerName: "No. of Users",
      minWidth: 100,
      flex: 0.25,
      type: "number",
      align: "left",
      headerAlign: "left",
      valueGetter: (params) => params?.row?.userCount ?? 0,
    },
    {
      headerName: "",
      minWidth: 80,
      flex: 0.2,
      renderCell: gridDepartmentAction,
      sortable: false,
      filterable: false,
    },
  ];

  const registrationRequestsGrid = [
    {
      field: "name",
      headerName: "Name",
      minWidth: 140,
      flex: 0.25,
      valueGetter: (params) => params?.row?.name ?? "",
    },
    {
      field: "employeeId",
      headerName: "Employee ID",
      minWidth: 120,
      flex: 0.15,
      valueGetter: (params) => params?.row?.employeeId ?? "",
    },
    {
      field: "mobileNumber",
      headerName: "Mobile Number",
      minWidth: 150,
      flex: 0.18,
      valueGetter: (params) => formatPhoneNumber(params?.row?.mobileNumber),
    },
    {
      field: "email",
      headerName: "Email",
      minWidth: 180,
      flex: 0.22,
      valueGetter: (params) => params?.row?.email ?? "",
    },
    {
      field: "departmentName",
      headerName: "Department",
      minWidth: 160,
      flex: 0.2,
      valueGetter: (params) => params?.row?.departmentName ?? "",
    },
    {
      field: "createdAt",
      headerName: "Created At",
      minWidth: 160,
      flex: 0.15,
      valueGetter: (params) => {
        if (!params?.row?.createdAt) return "";

        const date = new Date(params.row.createdAt);

        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "2-digit",
        });
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 200,
      flex: 0.2,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const uuid = params?.row?.uuid;
        const isApproving = !!requestActionLoading?.[uuid]?.approve;
        const isRejecting = !!requestActionLoading?.[uuid]?.reject || (rejectSubmitting && rejectDialogRequest?.uuid === uuid);

        const onApprove = async () => {
          if (!uuid) return;
          // Approval flow: open user creation with prefilled data.
          navigate(`/${Links.ADD_USER}/`, {
            state: {
              enableEditing: true,
              registrationRequestUuid: uuid,
              registrationRequest: {
                uuid,
                name: params?.row?.name,
                employeeId: params?.row?.employeeId,
                mobileNumber: params?.row?.mobileNumber,
                email: params?.row?.email,
                departmentId: params?.row?.departmentId,
                departmentName: params?.row?.departmentName,
              },
            },
          });
        };

        const onReject = () => {
          if (!uuid) return;
          openRejectDialog(params.row);
        };

        return (
          <Stack direction="row" spacing={1}>
            <LoadingButton
              size="small"
              variant="contained"
              // color="success"
              loading={isApproving}
              disabled={isApproving || isRejecting}
              onClick={onApprove}
              sx={{ textTransform: "none" }}
            >
              Approve
            </LoadingButton>
            <LoadingButton
              size="small"
              variant="outlined"
              color="error"
              loading={isRejecting}
              disabled={isApproving || isRejecting}
              onClick={onReject}
              sx={{
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "error.main",
                  color: "#fff",
                },
              }}
            >
              Reject
            </LoadingButton>
          </Stack>
        );
      },
    },
  ];
  if (isLoading) {
    return (
      <div
        style={{
          textAlign: "center",
          paddingTop: 200,
        }}
      >
        <CircularProgress color="secondary" />
      </div>
    );
  }

  return (
    <div>
      <Box sx={{ mx: 2, mt: 2 }}>
        <Paper sx={{ borderRadius: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, nextValue) => setActiveTab(nextValue)}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              px: 1,
            }}
          >
            <Tab
              value="users"
              label="Users"
              sx={{
                fontWeight: 700,
                fontSize: 14,
                textTransform: "capitalize",
              }}
            />

            <Tab
              value="departments"
              label="Departments"
              sx={{
                fontWeight: 700,
                fontSize: 14,
                textTransform: "capitalize",
              }}
            />
          </Tabs>

          {activeTab === "users" ? (
            <Box sx={{ p: 1.5 }}>
              <Stack direction="row" sx={{ mb: 2, width: "100%" }}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  fullWidth
                  value={userStatusTab}
                  onChange={(e, nextValue) => {
                    if (nextValue) setUserStatusTab(nextValue);
                  }}
                  sx={{
                    width: "100%",
                    bgcolor: "background.paper",
                    borderRadius: 2,

                    "& .MuiToggleButton-root": {
                      flex: 1,
                      py: 1,
                      textTransform: "none",
                      fontWeight: 600,
                    },

                    "& .MuiToggleButton-root.Mui-selected": {
                      bgcolor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      "&:hover": {
                        bgcolor: theme.palette.primary.dark,
                      },
                    },
                  }}
                >
                  <ToggleButton value="active" sx={{ fontSize: "0.75rem" }}>
                    Active ({activeUsersCount})
                  </ToggleButton>
                  <ToggleButton value="inactive" sx={{ fontSize: "0.75rem" }}>
                    Inactive ({inactiveUsersCount})
                  </ToggleButton>
                  <ToggleButton value="requests" sx={{ fontSize: "0.75rem" }}>
                    New requests ({newRequestsCount})
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              {userStatusTab === "requests" ? (
                <>
                  {requestsLoading ? (
                    <TableSkeleton rows={8} columns={registrationRequestsGrid.length} />
                  ) : requestsError ? (
                    <Typography color="error">{requestsError}</Typography>
                  ) : !registrationRequests.length ? (
                    <Paper
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        p: 2,
                        minHeight: 120,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography>No new requests.</Typography>
                    </Paper>
                  ) : (
                    <>
                      <Stack direction="row" sx={{ mb: 2, width: "100%" }} justifyContent="flex-start">
                        <OutlinedInput
                          id="outlined-adornment-requests-search"
                          value={requestsSearchValue}
                          onChange={(event) => handleRequestsSearchChange(event)}
                          placeholder="Search..."
                          startAdornment={
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          }
                          endAdornment={
                            requestsSearchValue ? (
                              <InputAdornment position="end">
                                <IconButton onClick={handleClearRequestsSearch} edge="end" aria-label="clear requests search">
                                  <ClearIcon />
                                </IconButton>
                              </InputAdornment>
                            ) : null
                          }
                          sx={{ minWidth: 300, maxWidth: 600, height: 35 }}
                        />
                      </Stack>
                      <DataGridTable
                        rowId={"uuid"}
                        data={filteredRegistrationRequestsForGrid}
                        columnAttributes={registrationRequestsGrid}
                        loading={requestsLoading}
                        onSelectRow={() => {}}
                      />
                    </>
                  )}
                </>
              ) : (
                <>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" my={1}>
                    <Stack direction={"row"} flexWrap={"wrap"} alignItems={"center"} gap={2} my={1}>
                      <Box>
                        <Button
                          startIcon={<AddIcon />}
                          sx={{ height: "self" }}
                          autoCapitalize="none"
                          variant="contained"
                          onClick={() => navigate(`/${Links.ADD_USER}/`)}
                        >
                          Add
                        </Button>
                      </Box>
                      <OutlinedInput
                        id="outlined-adornment-users-search"
                        value={searchValue}
                        onChange={(event) => handleSearchChange(event)}
                        placeholder="Search..."
                        startAdornment={
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        }
                        endAdornment={
                          searchValue ? (
                            <InputAdornment position="end">
                              <IconButton onClick={handleClearUsersSearch} edge="end" aria-label="clear search">
                                <ClearIcon />
                              </IconButton>
                            </InputAdornment>
                          ) : null
                        }
                        sx={{ minWidth: 300, maxWidth: 600, height: 35 }}
                      />
                      <Button sx={{ height: "self", textTransform: "none" }} variant="contained" onClick={clearAllUsersGridFilters}>
                        Clear Filters
                      </Button>
                    </Stack>
                    <Tooltip title={canExportUsersCsv ? "Export to CSV" : "No records to export"}>
                      <span>
                        <Button
                          startIcon={<DownloadIcon />}
                          sx={{ height: "self", mr: 2 }}
                          autoCapitalize="none"
                          variant="contained"
                          onClick={downloadCsv}
                          disabled={!canExportUsersCsv}
                        >
                          Export to csv
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                  {isLoading ? (
                    <TableSkeleton rows={8} columns={userGrid.length} />
                  ) : (
                    <DataGridTable rowId={"userId"} data={filteredUsersForGrid} columnAttributes={userGrid} onSelectRow={() => {}} />
                  )}
                </>
              )}
            </Box>
          ) : (
            <Box sx={{ p: 1.5 }}>
              {departmentsLoading ? (
                <TableSkeleton rows={8} columns={departmentGrid.length} />
              ) : departmentsError ? (
                <Typography color="error">{departmentsError}</Typography>
              ) : (
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" my={1}>
                    <Stack direction={"row"} gap={1} alignItems={"center"} my={1}>
                      <Box>
                        <Button
                          startIcon={<AddIcon />}
                          sx={{ height: "self" }}
                          autoCapitalize="none"
                          variant="contained"
                          onClick={() => setCreateDeptDialogOpen(true)}
                        >
                          Add
                        </Button>
                      </Box>
                      <OutlinedInput
                        id="outlined-adornment-departments-search"
                        value={departmentSearchValue}
                        onChange={(event) => handleDepartmentSearchChange(event)}
                        placeholder="Search..."
                        startAdornment={
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        }
                        endAdornment={
                          departmentSearchValue ? (
                            <InputAdornment position="end">
                              <IconButton onClick={handleClearDepartmentSearch} edge="end" aria-label="clear departments search">
                                <ClearIcon />
                              </IconButton>
                            </InputAdornment>
                          ) : null
                        }
                        sx={{ minWidth: 300, maxWidth: 600, height: 35 }}
                      />
                    </Stack>
                  </Stack>

                  {!departments.length ? (
                    <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
                      <Typography>No departments found.</Typography>
                    </Paper>
                  ) : (
                    <DataGridTable
                      rowId={"id"}
                      data={filteredDepartmentsForGrid}
                      columnAttributes={departmentGrid}
                      onSelectRow={() => {}}
                    />
                  )}
                </Box>
              )}
            </Box>
          )}
        </Paper>
      </Box>

      <DepartmentFormDialog
        open={createDeptDialogOpen}
        mode="create"
        name={newDepartmentName}
        onNameChange={setNewDepartmentName}
        onClose={closeCreateDeptDialog}
        onSubmit={submitCreateDepartment}
        submitting={createDeptSubmitting}
      />

      <DepartmentFormDialog
        open={editDeptDialogOpen}
        mode="edit"
        name={editDeptNameInput}
        onNameChange={setEditDeptNameInput}
        onClose={closeEditDeptDialog}
        onSubmit={submitEditDepartment}
        submitting={editDeptSubmitting}
      />

      <RegistrationNotifyDialog
        open={rejectNotifyDialog.open}
        onClose={() =>
          setRejectNotifyDialog({
            open: false,
            message: "",
            mobileNumber: "",
            recipientName: "",
            recipientMeta: "",
          })
        }
        title="Registration rejected"
        subtitle="Share the rejection details with the applicant."
        variant="danger"
        recipientName={rejectNotifyDialog.recipientName}
        recipientPhone={rejectNotifyDialog.mobileNumber}
        recipientMeta={rejectNotifyDialog.recipientMeta}
        message={rejectNotifyDialog.message}
        doneLabel="Close"
      />

      <RejectRegistrationDialog
        open={rejectDialogOpen}
        request={rejectDialogRequest}
        reason={rejectReason}
        reasonError={rejectReasonError}
        submitting={rejectSubmitting}
        onClose={closeRejectDialog}
        onReasonChange={(value) => {
          setRejectReason(value);
          if (rejectReasonError) setRejectReasonError("");
        }}
        onSubmit={submitRejectRegistrationRequest}
      />

      <DeleteDepartmentDialog
        open={deleteDeptDialogOpen}
        department={deleteDeptRow}
        users={deleteDeptUsers}
        usersLoading={deleteDeptUsersLoading}
        usersError={deleteDeptUsersError}
        submitting={deleteDeptSubmitting}
        onClose={closeDeleteDeptDialog}
        onSubmit={submitDeleteDepartment}
      />
    </div>
  );
};
export default Users;
